import { useTranslation } from 'react-i18next';
import type {
  PageDescriptor,
  ActionDescriptor,
  PageSectionDescriptor,
  PageSectionType,
  FormDescriptor,
  FormSubmissionResult,
  FormSubmissionContext,
  TranslationKey,
} from '@finance/contracts';
import { derivePageActionsFromRoute } from '@finance/contracts';
import { Page, PageSection, AppContentContainer, pageWidthForContract, type SectionVariant, type SectionPadding } from '../layout';
import { TableRenderer } from './TableRenderer.tsx';
import type { TableActionContext } from './table/table-renderer-props.ts';
import type { ActionExecutionContext } from '../../runtime/actions/action-executor.ts';
import { FormRenderer } from './FormRenderer.tsx';
import { WorkflowRenderer } from './WorkflowRenderer.tsx';
import { DashboardRenderer, type DashboardWidgetResult } from './DashboardRenderer.tsx';
import { ReportRenderer } from './ReportRenderer.tsx';
import { NavigationHubRenderer } from './NavigationHubRenderer.tsx';
import { SurfaceRenderer } from './SurfaceRenderer.tsx';
import { ErrorState, LoadingState, ForbiddenState, NotFoundState } from '../states';
import { resolveTKey } from '../../i18n/resolve-key';

const DEFAULT_SECTION_VARIANT: Record<PageSectionType, SectionVariant> = {
  HEADER: 'plain',
  TABLE: 'surface',
  FORM: 'surface',
  DETAIL: 'surface',
  WORKFLOW: 'surface',
  DASHBOARD: 'plain',
  REPORT: 'surface',
  NAVIGATION_HUB: 'plain',
  CUSTOM: 'surface',
};

const DEFAULT_SECTION_PADDING: Record<PageSectionType, SectionPadding> = {
  HEADER: 'standard',
  TABLE: 'none',
  FORM: 'standard',
  DETAIL: 'standard',
  WORKFLOW: 'standard',
  DASHBOARD: 'none',
  REPORT: 'none',
  NAVIGATION_HUB: 'none',
  CUSTOM: 'standard',
};

export interface PageRendererProps {
  contract: PageDescriptor;
  data?: Record<string, unknown>;
  tables?: Record<string, Record<string, unknown>[]>;
  /** Dashboard widget results keyed by dashboard section id */
  dashboardWidgetResults?: Record<string, DashboardWidgetResult[]>;
  /** Dashboard source-as-of timestamps keyed by dashboard section id */
  dashboardSourceAsOf?: Record<string, string | null>;
  /** Dashboard partial-data flags keyed by dashboard section id */
  dashboardPartialData?: Record<string, boolean>;
  /** Report parameter forms keyed by report section id */
  reportParameterForms?: Record<string, FormDescriptor>;
  /** Report parameters keyed by report section id */
  reportParameters?: Record<string, Record<string, unknown>>;
  /** Report state keyed by report section id */
  reportState?: Record<string, 'CURRENT' | 'PARTIAL' | 'EMPTY'>;
  /** Report partial-data flags keyed by report section id */
  reportPartialData?: Record<string, boolean>;
  /** Report source-as-of keyed by report section id */
  reportSourceAsOf?: Record<string, string | null>;
  /** Report summaries keyed by report section id */
  reportSummary?: Record<string, Record<string, unknown>>;
  /** Report lineage keyed by report section id */
  reportLineage?: Record<string, Record<string, unknown>>;
  /** Report pending flags keyed by report section id */
  reportPending?: Record<string, boolean>;
  /** Report run handlers keyed by report section id */
  onReportRun?: Record<string, (parameters: Record<string, unknown>) => Promise<FormSubmissionResult>>;
  /** Form initial values keyed by form section id */
  formInitialValues?: Record<string, Record<string, unknown>>;
  /** Form loading-initial flags keyed by form section id */
  formLoadingInitial?: Record<string, boolean>;
  /** Form record versions keyed by form section id */
  formRecordVersion?: Record<string, number>;
  loading?: boolean;
  /** Compatibility shape retained; user-facing error content remains contract-owned. */
  error?: boolean | { title?: string; message?: string };
  forbidden?: boolean;
  notFound?: boolean;
  onAction?: (action: ActionDescriptor, context?: ActionExecutionContext) => void | Promise<void>;
  onSubmit?: (
    commandId: string,
    values: Record<string, unknown>,
    context: FormSubmissionContext,
  ) => Promise<FormSubmissionResult>;
}

function sectionTitleKey(section: PageSectionDescriptor): TranslationKey | undefined {
  if (section.titleKey) return section.titleKey;
  if (section.sectionType === 'REPORT') return section.report?.titleKey;
  return undefined;
}

function sectionDescriptionKey(section: PageSectionDescriptor): TranslationKey | undefined {
  if (section.sectionType === 'REPORT') return section.report?.descriptionKey;
  return undefined;
}

export function PageRenderer({
  contract,
  data: _data,
  tables,
  dashboardWidgetResults,
  dashboardSourceAsOf,
  dashboardPartialData,
  reportParameterForms,
  reportParameters,
  reportState,
  reportPartialData,
  reportSourceAsOf,
  reportSummary,
  reportLineage,
  reportPending,
  onReportRun,
  formInitialValues,
  formLoadingInitial,
  formRecordVersion,
  loading,
  error,
  forbidden,
  notFound,
  onAction,
  onSubmit,
}: PageRendererProps) {
  const { t } = useTranslation();

  if (loading) return <LoadingState message={t(resolveTKey(contract.states.loading.messageKey))} />;
  if (forbidden) return <ForbiddenState title={t(resolveTKey(contract.states.forbidden.titleKey))} message={t(resolveTKey(contract.states.forbidden.messageKey))} />;
  if (notFound) return <NotFoundState title={t(resolveTKey(contract.states.notFound.titleKey))} message={t(resolveTKey(contract.states.notFound.messageKey))} />;
  if (error) return <ErrorState title={t(resolveTKey(contract.states.error.titleKey))} message={t(resolveTKey(contract.states.error.messageKey))} />;

  const pageWidth = pageWidthForContract(contract);
  const presentationStyle = contract.presentation?.sectionStyle;
  const pageActions = contract.pageActions ?? derivePageActionsFromRoute(contract.routeId);

  return (
    <AppContentContainer width={pageWidth}>
      <Page
        title={t(resolveTKey(contract.titleKey))}
        subtitle={contract.subtitleKey ? t(resolveTKey(contract.subtitleKey)) : undefined}
        actions={pageActions}
        onAction={(action) => onAction?.(action)}
      >
        {contract.sections.map((section) => {
          const sectionVariant = presentationStyle ?? DEFAULT_SECTION_VARIANT[section.sectionType];
          const sectionPadding = DEFAULT_SECTION_PADDING[section.sectionType];
          const titleKey = sectionTitleKey(section);
          const descriptionKey = sectionDescriptionKey(section);

          return (
            <PageSection
              key={section.id}
              id={section.id}
              title={titleKey ? t(resolveTKey(titleKey)) : undefined}
              description={descriptionKey ? t(resolveTKey(descriptionKey)) : undefined}
              actions={section.sectionActions}
              variant={sectionVariant}
              padding={sectionPadding}
              onAction={(action) => onAction?.(action)}
            >
              {section.sectionType === 'TABLE' && section.table && (
                <TableRenderer
                  contract={section.table}
                  data={tables?.[section.table.id] ?? []}
                  onAction={(resolved, context) => {
                    if (!resolved) return;
                    const actionContext: ActionExecutionContext = {
                      source: context.source === 'page-action' ? 'page' : 'table-row',
                      confirmationHandled: context.confirmationHandled,
                      confirmationValues: context.confirmationValues,
                      tableId: context.tableId,
                      rowId: context.rowId,
                      rowData: context.row,
                      selectedIds: context.selectedIds,
                      savedViewId: context.savedViewId,
                      queryFingerprint: context.queryFingerprint,
                    };
                    return onAction?.(resolved.action, actionContext);
                  }}
                />
              )}
              {section.sectionType === 'FORM' && section.form && (
                <FormRenderer
                  contract={section.form}
                  onSubmit={onSubmit}
                  onAction={onAction}
                  initialValues={formInitialValues?.[section.id]}
                  loadingInitialValues={formLoadingInitial?.[section.id]}
                  recordVersion={formRecordVersion?.[section.id]}
                />
              )}
              {section.sectionType === 'WORKFLOW' && section.workflow && (
                <WorkflowRenderer contract={section.workflow} onAction={onAction} />
              )}
              {section.sectionType === 'DASHBOARD' && section.dashboard && (
                <DashboardRenderer
                  contract={section.dashboard}
                  widgetResults={dashboardWidgetResults?.[section.id]}
                  sourceAsOf={dashboardSourceAsOf?.[section.id]}
                  partialData={dashboardPartialData?.[section.id]}
                  tableData={tables}
                />
              )}
              {section.sectionType === 'REPORT' && section.report && (
                <ReportRenderer
                  contract={section.report}
                  data={tables?.[section.report.id] ?? []}
                  parameterForm={reportParameterForms?.[section.id]}
                  parameters={reportParameters?.[section.id]}
                  state={reportState?.[section.id]}
                  partialData={reportPartialData?.[section.id]}
                  sourceAsOf={reportSourceAsOf?.[section.id]}
                  summary={reportSummary?.[section.id]}
                  lineage={reportLineage?.[section.id]}
                  pending={reportPending?.[section.id]}
                  onRun={onReportRun?.[section.id]}
                  onAction={(action) => onAction?.(action)}
                />
              )}
              {section.sectionType === 'NAVIGATION_HUB' && section.navigationHub && (
                <NavigationHubRenderer hub={section.navigationHub} />
              )}
              {(section.sectionType === 'HEADER' || section.sectionType === 'DETAIL' || section.sectionType === 'CUSTOM') && section.surface && (
                <SurfaceRenderer
                  surface={section.surface}
                  tables={tables}
                  onAction={onAction as ((action: ActionDescriptor, context?: Record<string, unknown>) => void) | undefined}
                  onSubmit={onSubmit}
                />
              )}
            </PageSection>
          );
        })}
      </Page>
    </AppContentContainer>
  );
}
